import {ChangeEvent, FC, useEffect, useMemo, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import * as XLSX from 'xlsx'
import {useAuth} from '../../auth'
import {useProcurementPermissions} from '../core/permissionAccess'
import {PROCUREMENT_PERMS} from '../core/permissions'
import {libraryUiScope} from '../core/procurementLibraryUiScope'
import {
  createGeneralInventoryItem,
  deleteInventory,
  getCompanyGroupAdmins,
  getPartStorageLocations,
  getVesselsByCompany,
  getInventoryItemSubCategories,
  getGeneralInventoryItems,
  updateInventory,
} from '../core/_requests'
import {
  CompanyGroupAdmin,
  CreateInventoryRequest,
  UpdateInventoryRequest,
  Vessel,
  InventoryItemSubCategory,
  InventoryItem,
  PartStorageLocation,
} from '../core/_models'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface GeneralItemModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  vesselId?: number | null
  allowRead?: boolean
  allowCreate?: boolean
  allowUpdate?: boolean
  allowDelete?: boolean
  allowImport?: boolean
  allowExport?: boolean
  useProcurementAccessControl?: boolean
}

const UNIT_OPTIONS = ['PCS', 'KG', 'LTR', 'SET', 'BOX', 'PKT', 'PAIR']

const staticSubCategoryOptions = [
  {value: 'IMPA_STORES', label: 'IMPA Stores'},
  {value: 'CHEMICALS', label: 'Chemicals'},
  {value: 'GASES_LUBE', label: 'Gases & Lube oil'},
]

type ImportPreviewRow = {
  rowNumber: number
  itemName: string
  unit: string
  subCategory: string
  currentQty: number
  reorderLevel: number
  minThreshold: number
  maxThreshold: number
  unitCost: number
  location: string
  inventoryItemSubCategoryId: number | null
  subCategoryName: string | null
  valid: boolean
  error?: string
}

const GeneralItemModal: FC<GeneralItemModalProps> = ({
  visible,
  onClose,
  onSubmit,
  vesselId,
  allowRead = true,
  allowCreate,
  allowUpdate = true,
  allowDelete = true,
  allowImport = true,
  allowExport = true,
  useProcurementAccessControl = true,
}) => {
  const {currentUser} = useAuth()
  const {getScope} = useProcurementPermissions()
  const generalLibraryScope = libraryUiScope(
    getScope,
    [
      PROCUREMENT_PERMS.CREATE_LIBRARY_CATEGORY,
      PROCUREMENT_PERMS.UPDATE_LIBRARY_CATEGORY,
      PROCUREMENT_PERMS.DELETE_LIBRARY_CATEGORY,
    ],
    PROCUREMENT_PERMS.READ_LIBRARY_CATEGORY,
  )
  const isGlobalLibraryUser = useProcurementAccessControl && generalLibraryScope === 'ALL'
  const canCreateEntries = allowCreate ?? true
  const canUpdateEntries = allowUpdate
  const canDeleteEntries = allowDelete
  const canImportEntries = allowImport
  const canExportEntries = allowExport

  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState('')
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedVesselId, setSelectedVesselId] = useState<string>('')

  const [itemName, setItemName] = useState('')
  const [unit, setUnit] = useState('PCS')
  const [currentQty, setCurrentQty] = useState('')
  const [reorderLevel, setReorderLevel] = useState('')
  const [minThreshold, setMinThreshold] = useState('')
  const [maxThreshold, setMaxThreshold] = useState('')
  const [unitCost, setUnitCost] = useState('')
  const [location, setLocation] = useState('')
  const [category, setCategory] = useState('STORES')
  const [subCategoryId, setSubCategoryId] = useState<string>('')

  const [subCategories, setSubCategories] = useState<InventoryItemSubCategory[]>([])
  const [storageLocations, setStorageLocations] = useState<PartStorageLocation[]>([])
  const [generalItems, setGeneralItems] = useState<InventoryItem[]>([])
  const [isLoadingItems, setIsLoadingItems] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null)
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [itemsListPage, setItemsListPage] = useState(1)
  const [itemsListRowsPerPage, setItemsListRowsPerPage] = useState(10)
  const [itemsSearchTerm, setItemsSearchTerm] = useState('')
  const [itemsSubCategoryFilter, setItemsSubCategoryFilter] = useState('')
  const [itemsLocationFilter, setItemsLocationFilter] = useState('')
  const [importPreviewRows, setImportPreviewRows] = useState<ImportPreviewRow[]>([])
  const [importFileName, setImportFileName] = useState('')
  const [showImportPreview, setShowImportPreview] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!visible) return

    if (vesselId) {
      setSelectedVesselId(String(vesselId))
    }

    loadSubCategories()
    loadStorageLocations()
    loadCompanies()

    if (!isGlobalLibraryUser) {
      loadVesselsForCurrentUser()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, vesselId])

  useEffect(() => {
    if (!visible) return
    loadGeneralItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, selectedVesselId, vesselId])

  useEffect(() => {
    if (!canCreateEntries && showForm) {
      setShowForm(false)
    }
  }, [canCreateEntries, showForm])

  useEffect(() => {
    if (!canUpdateEntries && editingItem) {
      setEditingItem(null)
    }
  }, [canUpdateEntries, editingItem])

  useEffect(() => {
    if (!canDeleteEntries && editingItem) {
      setEditingItem(null)
    }
  }, [canDeleteEntries, editingItem])

  useEffect(() => {
    setItemsListPage(1)
  }, [visible, generalItems.length, itemsListRowsPerPage, itemsSearchTerm, itemsSubCategoryFilter, itemsLocationFilter])

  const filteredGeneralItems = useMemo(() => {
    const normalizedSearch = itemsSearchTerm.trim().toLowerCase()
    const normalizedSubCategory = itemsSubCategoryFilter.trim().toLowerCase()
    const normalizedLocation = itemsLocationFilter.trim().toLowerCase()

    return generalItems.filter((item) => {
      const itemName = String(item.manualItemName || (item as any).name || item.part?.name || '').toLowerCase()
      const unit = String(item.unitOfMeasurement || (item as any).uom?.code || (item as any).uom?.name || '').toLowerCase()
      const subCategory = String(
        (item as any).inventoryItemSubCategory?.name || (item as any).subCategoryName || ''
      ).toLowerCase()
      const itemLocation = String(item.location || '').toLowerCase()

      const matchesSearch =
        !normalizedSearch ||
        itemName.includes(normalizedSearch) ||
        unit.includes(normalizedSearch) ||
        subCategory.includes(normalizedSearch) ||
        itemLocation.includes(normalizedSearch)

      const matchesSubCategory = !normalizedSubCategory || subCategory === normalizedSubCategory
      const matchesLocation = !normalizedLocation || itemLocation === normalizedLocation

      return matchesSearch && matchesSubCategory && matchesLocation
    })
  }, [generalItems, itemsSearchTerm, itemsSubCategoryFilter, itemsLocationFilter])

  const subCategoryFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          generalItems
            .map((item) => (item as any).inventoryItemSubCategory?.name || (item as any).subCategoryName || '')
            .map((value) => String(value).trim())
            .filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [generalItems]
  )

  const locationFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          generalItems
            .map((item) => String(item.location || '').trim())
            .filter(Boolean)
        )
      ).sort((left, right) => left.localeCompare(right)),
    [generalItems]
  )

  const itemsListTotalPages = Math.max(1, Math.ceil(filteredGeneralItems.length / itemsListRowsPerPage))
  const pagedGeneralItems = useMemo(() => {
    const start = (itemsListPage - 1) * itemsListRowsPerPage
    return filteredGeneralItems.slice(start, start + itemsListRowsPerPage)
  }, [filteredGeneralItems, itemsListPage, itemsListRowsPerPage])

  useEffect(() => {
    if (itemsListPage > itemsListTotalPages) {
      setItemsListPage(itemsListTotalPages)
    }
  }, [itemsListPage, itemsListTotalPages])

  const loadCompanies = async () => {
    try {
      if (!useProcurementAccessControl || !isGlobalLibraryUser) {
        setCompanies([])
        return
      }

      const data = await getCompanyGroupAdmins()
      setCompanies(data || [])
    } catch (error) {
      console.error('Error loading companies for general items:', error)
      setCompanies([])
    }
  }

  const loadVesselsForCurrentUser = async () => {
    try {
      if (!useProcurementAccessControl) {
        setVessels([])
        return
      }

      const companyUsername =
        (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username

      if (!companyUsername) {
        setVessels([])
        return
      }

      const list = await getVesselsByCompany(companyUsername)
      setVessels(list || [])
    } catch (error) {
      console.error('Error loading vessels for general items:', error)
      setVessels([])
    }
  }

  const loadVesselsForCompany = async (companyId: number) => {
    try {
      const selected = companies.find((c) => c.id === companyId)

      if (!selected?.username) {
        setVessels([])
        return
      }

      const list = await getVesselsByCompany(selected.username)
      setVessels(list || [])
    } catch (error) {
      console.error('Error loading vessels for selected company:', error)
      setVessels([])
    }
  }

  useEffect(() => {
    if (!isGlobalLibraryUser) return

    if (!selectedCompanyId) {
      setVessels([])
      setSelectedVesselId('')
      return
    }

    setSelectedVesselId('')
    loadVesselsForCompany(Number(selectedCompanyId))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, companies])

  const loadSubCategories = async () => {
    try {
      const data = await getInventoryItemSubCategories(true)
      setSubCategories(data || [])
    } catch (err) {
      console.error('Error loading sub categories for general items:', err)
      setSubCategories([])
    }
  }

  const loadStorageLocations = async () => {
    try {
      const data = await getPartStorageLocations()
      setStorageLocations(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error loading storage locations for general items:', error)
      setStorageLocations([])
    }
  }

  const loadGeneralItems = async () => {
    try {
      setIsLoadingItems(true)
      const effectiveVesselId = selectedVesselId ? Number(selectedVesselId) : vesselId
      const items = await getGeneralInventoryItems(effectiveVesselId)
      setGeneralItems(items || [])
    } catch (error) {
      console.error('Error loading general items:', error)
      setGeneralItems([])
    } finally {
      setIsLoadingItems(false)
    }
  }

  const resetForm = () => {
    setSelectedCompanyId('')
    setItemName('')
    setUnit('PCS')
    setCurrentQty('')
    setReorderLevel('')
    setMinThreshold('')
    setMaxThreshold('')
    setUnitCost('')
    setLocation('')
    setCategory('STORES')
    setSubCategoryId('')
    setErrors({})
    setShowForm(false)
    setEditingItem(null)

    if (!vesselId) {
      setSelectedVesselId('')
    }
  }

  const resetAddForm = () => {
    setItemName('')
    setUnit('PCS')
    setCurrentQty('')
    setReorderLevel('')
    setMinThreshold('')
    setMaxThreshold('')
    setUnitCost('')
    setLocation('')
    setCategory('STORES')
    setSubCategoryId('')
    setErrors({})
    setShowForm(false)
    setEditingItem(null)
  }

  const openEditForm = (item: InventoryItem) => {
    setEditingItem(item)
    setShowForm(true)
    setErrors({})
    setItemName(item.manualItemName || (item as any).name || item.part?.name || '')
    setUnit(item.unitOfMeasurement || '')
    setCurrentQty(String(item.currentQty ?? 0))
    setReorderLevel(String(item.reorderLevel ?? 0))
    setMinThreshold(String(item.minThreshold ?? 0))
    setMaxThreshold(String(item.maxThreshold ?? 0))
    setUnitCost(String(item.unitCost ?? 0))
    setLocation(item.location || '')
    setCategory('STORES')

    const inventorySubCategoryId =
      item.inventoryItemSubCategoryId ?? item.inventoryItemSubCategory?.id ?? null
    setSubCategoryId(inventorySubCategoryId != null ? String(inventorySubCategoryId) : '')

    if (!vesselId && item.vesselId != null) {
      setSelectedVesselId(String(item.vesselId))
    }
  }

  const handleDeleteItem = async (item: InventoryItem) => {
    if (!item.id) return
    if (!window.confirm('Delete this general item?')) return

    try {
      await deleteInventory(item.id)
      toast.success('General item deleted successfully')
      await loadGeneralItems()
      if (editingItem?.id === item.id) {
        resetAddForm()
      }
      onSubmit()
    } catch (error: any) {
      console.error('Error deleting general item:', error)
      toast.error('Failed to delete general item')
    }
  }

  const handleClose = () => {
    resetForm()
    setItemsSearchTerm('')
    setItemsSubCategoryFilter('')
    setItemsLocationFilter('')
    setImportPreviewRows([])
    setImportFileName('')
    setShowImportPreview(false)
    onClose()
  }

  const normalizeHeader = (value: unknown) =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')

  const parseNumberValue = (value: unknown) => {
    if (value == null || value === '') return 0
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const resolveSubCategoryFromImport = (rawValue: unknown) => {
    const normalizedValue = String(rawValue ?? '').trim()
    if (!normalizedValue) {
      return {inventoryItemSubCategoryId: null, subCategoryName: null}
    }

    const normalizedKey = normalizedValue.toLowerCase()
    const matchedSubCategory = subCategories.find(
      (subCategory) => subCategory.name.trim().toLowerCase() === normalizedKey
    )

    if (matchedSubCategory) {
      return {
        inventoryItemSubCategoryId: matchedSubCategory.id,
        subCategoryName: matchedSubCategory.name,
      }
    }

    const matchedStaticOption = staticSubCategoryOptions.find(
      (option) => option.label.trim().toLowerCase() === normalizedKey
    )

    if (matchedStaticOption) {
      return {
        inventoryItemSubCategoryId: null,
        subCategoryName: matchedStaticOption.label,
      }
    }

    return {
      inventoryItemSubCategoryId: null,
      subCategoryName: normalizedValue,
    }
  }

  const selectedStorageLocation = useMemo(
    () => storageLocations.find((storageLocation) => storageLocation.name === location) || null,
    [storageLocations, location]
  )

  const selectedStorageSubLocations = useMemo(
    () =>
      (selectedStorageLocation?.subLocations || [])
        .map((subLocation) => subLocation.name)
        .filter(Boolean)
        .join(', '),
    [selectedStorageLocation]
  )

  const selectedStorageSegments = useMemo(
    () =>
      Array.from(
        new Set(
          (selectedStorageLocation?.subLocations || [])
            .flatMap((subLocation) => subLocation.segments || [])
            .map((segment) => segment.name)
            .filter(Boolean)
        )
      ).join(', '),
    [selectedStorageLocation]
  )

  const getEffectiveImportVesselId = () => {
    const effectiveVesselId = Number(selectedVesselId || vesselId || 0)
    return Number.isFinite(effectiveVesselId) && effectiveVesselId > 0 ? effectiveVesselId : null
  }

  const resetImportState = () => {
    setImportPreviewRows([])
    setImportFileName('')
    setShowImportPreview(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleImportFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const extension = file.name.toLowerCase()
    if (!extension.endsWith('.xlsx') && !extension.endsWith('.xls') && !extension.endsWith('.csv')) {
      toast.error('Please select an Excel (.xlsx, .xls) or CSV (.csv) file')
      event.target.value = ''
      return
    }

    const effectiveVesselId = getEffectiveImportVesselId()
    if (!effectiveVesselId) {
      toast.error('Please select a vessel before importing general items')
      event.target.value = ''
      return
    }

    try {
      const buffer = await file.arrayBuffer()
      const workbook = XLSX.read(buffer, {type: 'array'})
      const firstSheetName = workbook.SheetNames[0]

      if (!firstSheetName) {
        toast.error('The selected file is empty')
        event.target.value = ''
        return
      }

      const worksheet = workbook.Sheets[firstSheetName]
      const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, {
        defval: '',
      })

      if (rawRows.length === 0) {
        toast.error('No rows found in the selected file')
        event.target.value = ''
        return
      }

      const previewRows: ImportPreviewRow[] = rawRows.map((row, index) => {
        const normalizedRow = Object.entries(row).reduce<Record<string, unknown>>((acc, [key, value]) => {
          acc[normalizeHeader(key)] = value
          return acc
        }, {})

        const itemName = String(
          normalizedRow.itemname ?? normalizedRow.name ?? normalizedRow.item ?? ''
        ).trim()
        const unit = String(
          normalizedRow.unit ?? normalizedRow.uom ?? normalizedRow.unitofmeasurement ?? ''
        ).trim()
        const subCategoryRaw =
          normalizedRow.subcategory ?? normalizedRow.subcategoryoptional ?? normalizedRow.subcat ?? ''
        const resolvedSubCategory = resolveSubCategoryFromImport(subCategoryRaw)

        const rowErrors: string[] = []
        if (!itemName) rowErrors.push('Item Name is required')
        if (!unit) rowErrors.push('Unit is required')

        return {
          rowNumber: index + 2,
          itemName,
          unit,
          subCategory: String(subCategoryRaw ?? '').trim(),
          currentQty: parseNumberValue(normalizedRow.currentquantity ?? normalizedRow.currentqty ?? normalizedRow.qty),
          reorderLevel: parseNumberValue(normalizedRow.reorderlevel),
          minThreshold: parseNumberValue(normalizedRow.minthreshold),
          maxThreshold: parseNumberValue(normalizedRow.maxthreshold),
          unitCost: parseNumberValue(normalizedRow.unitcost),
          location: String(normalizedRow.location ?? '').trim(),
          inventoryItemSubCategoryId: resolvedSubCategory.inventoryItemSubCategoryId,
          subCategoryName: resolvedSubCategory.subCategoryName,
          valid: rowErrors.length === 0,
          error: rowErrors.join(', '),
        }
      })

      setImportPreviewRows(previewRows)
      setImportFileName(file.name)
      setShowImportPreview(true)
    } catch (error) {
      console.error('Error parsing general items import file:', error)
      toast.error('Failed to read the selected file')
      event.target.value = ''
    }
  }

  const handleImportItems = async () => {
    const effectiveVesselId = getEffectiveImportVesselId()
    if (!effectiveVesselId) {
      toast.error('Please select a vessel before importing general items')
      return
    }

    const validRows = importPreviewRows.filter((row) => row.valid)
    if (validRows.length === 0) {
      toast.error('No valid rows available to import')
      return
    }

    setIsImporting(true)

    try {
      let successCount = 0

      for (const row of validRows) {
        const payload: CreateInventoryRequest = {
          vesselId: effectiveVesselId,
          itemFor: 'GENERAL_STORES',
          manualItemName: row.itemName,
          unitOfMeasurement: row.unit,
          currentQty: row.currentQty,
          reorderLevel: row.reorderLevel,
          minThreshold: row.minThreshold,
          maxThreshold: row.maxThreshold,
          unitCost: row.unitCost,
          location: row.location,
          inventoryItemSubCategoryId: row.inventoryItemSubCategoryId,
          subCategoryName: row.subCategoryName,
        }

        await createGeneralInventoryItem(payload)
        successCount += 1
      }

      await loadGeneralItems()
      onSubmit()
      resetImportState()
      toast.success(
        `${successCount} general item${successCount === 1 ? '' : 's'} imported successfully`
      )
    } catch (error: any) {
      console.error('Error importing general items:', error)
      toast.error('Failed to import general items')
    } finally {
      setIsImporting(false)
    }
  }

  const handleExportItems = () => {
    if (filteredGeneralItems.length === 0) {
      toast.error('No general items available to export')
      return
    }

    const exportRows = filteredGeneralItems.map((item) => ({
      'Item Name': item.manualItemName || (item as any).name || item.part?.name || '',
      Unit:
        item.unitOfMeasurement || (item as any).uom?.code || (item as any).uom?.name || '',
      'Sub Category':
        (item as any).inventoryItemSubCategory?.name || (item as any).subCategoryName || '',
      'Current Quantity': item.currentQty ?? (item as any).quantity ?? 0,
      'Reorder Level': item.reorderLevel ?? 0,
      'Min Threshold': item.minThreshold ?? 0,
      'Max Threshold': item.maxThreshold ?? 0,
      'Unit Cost': item.unitCost ?? 0,
      Location: item.location || '',
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportRows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'General Items')
    XLSX.writeFile(workbook, 'technical_general_items_library.xlsx')
  }

  const validate = () => {
    const nextErrors: {[key: string]: string} = {}
    const vesselRequired = isGlobalLibraryUser || !vesselId

    if (isGlobalLibraryUser && !selectedCompanyId) {
      nextErrors.companyId = 'Company is required'
    }

    if (vesselRequired && !selectedVesselId) {
      nextErrors.vesselId = 'Vessel is required'
    }

    if (!itemName.trim()) {
      nextErrors.itemName = 'Item name is required'
    }

    if (!unit.trim()) {
      nextErrors.unit = 'Unit is required'
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setIsSubmitting(true)

    try {
      const basePayload = {
        vesselId: Number(selectedVesselId || vesselId),
        itemFor: 'GENERAL_STORES' as const,
        manualItemName: itemName.trim(),
        unitOfMeasurement: unit.trim(),
        currentQty: Number(currentQty || 0),
        reorderLevel: Number(reorderLevel || 0),
        minThreshold: Number(minThreshold || 0),
        maxThreshold: Number(maxThreshold || 0),
        unitCost: Number(unitCost || 0),
        location: location || '',
        inventoryItemSubCategoryId: subCategoryId ? Number(subCategoryId) : null,
        subCategoryName:
          subCategories.find((s) => s.id === Number(subCategoryId))?.name ||
          staticSubCategoryOptions.find((o) => o.value === subCategoryId)?.label ||
          null,
      }

      if (editingItem?.id) {
        const payload: UpdateInventoryRequest = basePayload
        await updateInventory(editingItem.id, payload)
        toast.success('General item updated successfully')
      } else {
        const payload: CreateInventoryRequest = basePayload
        await createGeneralInventoryItem(payload)
        toast.success('General item added to inventory')
      }
      await loadGeneralItems()
      onSubmit()
      resetAddForm()
    } catch (error: any) {
      console.error('Error saving general item:', error)
      toast.error(
        error.message || (editingItem ? 'Failed to update general item' : 'Failed to create general item')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!visible || !allowRead) return null

  const vesselSelectionLocked = Boolean(vesselId) && !isGlobalLibraryUser
  const isSuperadmin = isGlobalLibraryUser

  return (
    <div
      className='d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(2px)',
        padding: '24px',
        overflow: 'auto',
        overscrollBehavior: 'contain',
      }}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        role='dialog'
        aria-modal='true'
        style={{
          width: 'min(1400px, 96vw)',
          height: 'min(900px, 92vh)',
          background: '#ffffff',
          borderRadius: '18px',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.22)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexShrink: 0,
          }}
        >
          <div>
            <h5
              className='mb-1'
              style={{
                margin: 0,
                color: '#111827',
                fontSize: '1.2rem',
                fontWeight: 700,
              }}
            >
              General Items Library
            </h5>
            <div
              style={{
                color: '#6B7280',
                fontSize: '0.92rem',
              }}
            >
              Browse existing items and add a new general inventory item.
            </div>
          </div>

          <div className='d-flex align-items-center gap-2'>
            {canExportEntries && (
              <button
                type='button'
                className='btn btn-light-success btn-sm'
                onClick={handleExportItems}
                style={{
                  minWidth: '120px',
                  fontWeight: 600,
                }}
              >
                Export Excel
              </button>
            )}
            {canImportEntries && (
              <>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='.xlsx,.xls,.csv'
                  style={{display: 'none'}}
                  onChange={handleImportFileChange}
                />
                <button
                  type='button'
                  className='btn btn-light-primary btn-sm'
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    minWidth: '120px',
                    fontWeight: 600,
                  }}
                >
                  Import Excel
                </button>
              </>
            )}
            {canCreateEntries && (
                  <button
                    type='button'
                    className='btn btn_primary btn-sm'
                    onClick={() => {
                      if (showForm) {
                        resetAddForm()
                      } else {
                        setEditingItem(null)
                        setShowForm(true)
                      }
                    }}
                    style={{
                      minWidth: '150px',
                      fontWeight: 600,
                    }}
                  >
                    {showForm ? 'Hide Add Form' : 'Add General Item'}
                  </button>
                )}

            <button
              type='button'
              onClick={handleClose}
              className='btn btn-sm'
              style={{
                border: '1px solid #D1D5DB',
                background: '#fff',
                color: '#374151',
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                fontSize: '18px',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: showForm ? '1.35fr 1fr' : '1fr',
            gap: '0',
          }}
        >
          <div
            style={{
              padding: '20px 24px',
              borderRight: showForm ? '1px solid #E5E7EB' : 'none',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '14px',
                gap: '12px',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#111827',
                  }}
                >
                  Available Items
                </div>
                <div
                  style={{
                    fontSize: '0.88rem',
                    color: '#6B7280',
                  }}
                >
                  {isLoadingItems
                    ? 'Loading general items...'
                    : `${filteredGeneralItems.length} item${filteredGeneralItems.length === 1 ? '' : 's'} found`}
                </div>
              </div>
            </div>

            <div className='row g-3 mb-3'>
              <div className='col-md-5'>
                <input
                  type='text'
                  className='form-control'
                  value={itemsSearchTerm}
                  onChange={(e) => setItemsSearchTerm(e.target.value)}
                  placeholder='Search by item, unit, sub category, location'
                />
              </div>
              <div className='col-md-3'>
                <SearchableSelect
                  className='form-select'
                  value={itemsSubCategoryFilter}
                  onChange={(e) => setItemsSubCategoryFilter(e.target.value)}
                >
                  <option value=''>All Sub Categories</option>
                  {subCategoryFilterOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div className='col-md-3'>
                <SearchableSelect
                  className='form-select'
                  value={itemsLocationFilter}
                  onChange={(e) => setItemsLocationFilter(e.target.value)}
                >
                  <option value=''>All Locations</option>
                  {locationFilterOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div className='col-md-1'>
                <button
                  type='button'
                  className='btn btn-light w-100'
                  onClick={() => {
                    setItemsSearchTerm('')
                    setItemsSubCategoryFilter('')
                    setItemsLocationFilter('')
                  }}
                >
                  Clear
                </button>
              </div>
            </div>

            {showImportPreview && (
              <div
                style={{
                  marginBottom: '14px',
                  border: '1px solid #BFDBFE',
                  background: '#EFF6FF',
                  borderRadius: '12px',
                  padding: '14px 16px',
                }}
              >
                <div className='d-flex flex-wrap justify-content-between align-items-start gap-3'>
                  <div>
                    <div style={{fontSize: '0.95rem', fontWeight: 700, color: '#1E3A8A'}}>
                      Import Preview
                    </div>
                    <div style={{fontSize: '0.85rem', color: '#475569'}}>
                      {importFileName || 'Selected file'} • {importPreviewRows.length} row
                      {importPreviewRows.length === 1 ? '' : 's'} •{' '}
                      {importPreviewRows.filter((row) => row.valid).length} valid
                    </div>
                  </div>

                  <div className='d-flex gap-2'>
                    <button
                      type='button'
                      className='btn btn-sm btn-light'
                      onClick={resetImportState}
                      disabled={isImporting}
                    >
                      Cancel
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary'
                      onClick={handleImportItems}
                      disabled={isImporting || importPreviewRows.filter((row) => row.valid).length === 0}
                    >
                      {isImporting ? 'Importing...' : 'Import Items'}
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: '12px',
                    maxHeight: '260px',
                    overflow: 'auto',
                    background: '#fff',
                    borderRadius: '10px',
                    border: '1px solid #DBEAFE',
                  }}
                >
                  <table className='table table-sm align-middle mb-0'>
                    <thead style={{position: 'sticky', top: 0, background: '#F8FAFC', zIndex: 1}}>
                      <tr>
                        <th style={{padding: '10px 12px'}}>Row</th>
                        <th style={{padding: '10px 12px'}}>Item Name</th>
                        <th style={{padding: '10px 12px'}}>Unit</th>
                        <th style={{padding: '10px 12px'}}>Sub Category</th>
                        <th style={{padding: '10px 12px'}}>Qty</th>
                        <th style={{padding: '10px 12px'}}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreviewRows.map((row) => (
                        <tr key={`${row.rowNumber}-${row.itemName}`}>
                          <td style={{padding: '10px 12px'}}>{row.rowNumber}</td>
                          <td style={{padding: '10px 12px', fontWeight: 600}}>
                            {row.itemName || '-'}
                          </td>
                          <td style={{padding: '10px 12px'}}>{row.unit || '-'}</td>
                          <td style={{padding: '10px 12px'}}>
                            {row.subCategoryName || row.subCategory || '-'}
                          </td>
                          <td style={{padding: '10px 12px'}}>{row.currentQty}</td>
                          <td style={{padding: '10px 12px'}}>
                            <span className={`badge ${row.valid ? 'badge-light-success' : 'badge-light-danger'}`}>
                              {row.valid ? 'Ready' : row.error || 'Invalid'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  border: '1px solid #E5E7EB',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  background: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  overscrollBehavior: 'contain',
                }}
                onWheel={(e) => e.stopPropagation()}
              >
                <table className='table table-hover align-middle mb-0'>
                  <thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: '#F9FAFB',
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          minWidth: '260px',
                          padding: '14px 16px',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: '#111827',
                          background: '#F9FAFB',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        Item
                      </th>
                      <th
                        style={{
                          minWidth: '160px',
                          padding: '14px 16px',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: '#111827',
                          background: '#F9FAFB',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        Category
                      </th>
                      <th
                        style={{
                          minWidth: '200px',
                          padding: '14px 16px',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: '#111827',
                          background: '#F9FAFB',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        Sub Category
                      </th>
                      <th
                        style={{
                          minWidth: '110px',
                          padding: '14px 16px',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: '#111827',
                          background: '#F9FAFB',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        UOM
                      </th>
                      <th
                        style={{
                          minWidth: '100px',
                          padding: '14px 16px',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: '#111827',
                          background: '#F9FAFB',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        Qty
                      </th>
                      <th
                        style={{
                          minWidth: '220px',
                          padding: '14px 16px',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          color: '#111827',
                          background: '#F9FAFB',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        Location
                      </th>
                      {(canUpdateEntries || canDeleteEntries) && (
                        <th
                          style={{
                            minWidth: '180px',
                            padding: '14px 16px',
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em',
                            color: '#111827',
                            background: '#F9FAFB',
                            borderBottom: '1px solid #E5E7EB',
                          }}
                        >
                          Action
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {isLoadingItems && (
                      <tr>
                        <td colSpan={canUpdateEntries || canDeleteEntries ? 7 : 6} className='text-center text-muted py-5'>
                          Loading general items...
                        </td>
                      </tr>
                    )}

                    {!isLoadingItems && filteredGeneralItems.length === 0 && (
                      <tr>
                        <td colSpan={canUpdateEntries || canDeleteEntries ? 7 : 6} className='text-center text-muted py-5'>
                          No general items found.
                        </td>
                      </tr>
                    )}

                    {!isLoadingItems &&
                      pagedGeneralItems.map((gi) => (
                        <tr key={gi.id}>
                          <td style={{padding: '14px 16px', fontWeight: 600, color: '#111827'}}>
                            {gi.manualItemName || (gi as any).name || gi.part?.name || '-'}
                          </td>
                          <td style={{padding: '14px 16px', color: '#4B5563'}}>
                            {(gi as any).category?.name || (gi as any).inventoryCategoryName || 'Stores'}
                          </td>
                          <td style={{padding: '14px 16px', color: '#4B5563'}}>
                            {(gi as any).inventoryItemSubCategory?.name ||
                              (gi as any).subCategoryName ||
                              '-'}
                          </td>
                          <td style={{padding: '14px 16px', color: '#4B5563'}}>
                            {gi.unitOfMeasurement ||
                              (gi as any).uom?.code ||
                              (gi as any).uom?.name ||
                              '-'}
                          </td>
                          <td style={{padding: '14px 16px', color: '#4B5563'}}>
                            {gi.currentQty ?? (gi as any).quantity ?? '-'}
                          </td>
                          <td style={{padding: '14px 16px', color: '#4B5563'}}>
                            {gi.location || '-'}
                          </td>
                          {(canUpdateEntries || canDeleteEntries) && (
                            <td style={{padding: '14px 16px'}}>
                              <div className='d-flex gap-2'>
                                {canUpdateEntries && (
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    onClick={() => openEditForm(gi)}
                                  >
                                    Edit
                                  </button>
                                )}
                                {canDeleteEntries && (
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-danger'
                                    onClick={() => handleDeleteItem(gi)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {!isLoadingItems && filteredGeneralItems.length > 0 && (
                <div
                  className='d-flex justify-content-between align-items-center flex-shrink-0 border-top px-3 py-2'
                  style={{background: '#F9FAFB'}}
                >
                  <div className='d-flex align-items-center gap-2'>
                    <span className='text-muted small'>Rows / page</span>
                    <SearchableSelect
                      className='form-select form-select-sm'
                      style={{width: '72px'}}
                      value={itemsListRowsPerPage}
                      onChange={(e) => {
                        setItemsListRowsPerPage(Number(e.target.value))
                        setItemsListPage(1)
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </SearchableSelect>
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    <span className='text-muted small'>
                      Page {itemsListPage} / {itemsListTotalPages}
                    </span>
                    <button
                      type='button'
                      className='btn btn-sm btn-light'
                      disabled={itemsListPage <= 1}
                      onClick={() => setItemsListPage((p) => Math.max(1, p - 1))}
                    >
                      ‹
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-light'
                      disabled={itemsListPage >= itemsListTotalPages}
                      onClick={() => setItemsListPage((p) => Math.min(itemsListTotalPages, p + 1))}
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showForm && (
            <div
              style={{
                background: '#FAFBFC',
                padding: '20px 24px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <div style={{marginBottom: '16px'}}>
                <div
                  style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#111827',
                    marginBottom: '4px',
                  }}
                >
                  {editingItem ? 'Edit General Item' : 'Add General Item'}
                </div>
                <div
                  style={{
                    fontSize: '0.88rem',
                    color: '#6B7280',
                  }}
                >
                  {editingItem
                    ? 'Update the item details and save your changes.'
                    : 'Fill in the item details to add it to the general items inventory.'}
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  paddingRight: '4px',
                }}
              >
                <div className='row g-3'>
                  {isSuperadmin && (
                    <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Company</label>
                      <SearchableSelect
                        className={`form-select ${errors.companyId ? 'is-invalid' : ''}`}
                        value={selectedCompanyId}
                        onChange={(e) => setSelectedCompanyId(e.target.value)}
                      >
                        <option value=''>Select Company</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </SearchableSelect>
                      {errors.companyId && <div className='invalid-feedback'>{errors.companyId}</div>}
                    </div>
                  )}

                  {(isSuperadmin || !vesselId) && (
                    <div className='col-md-6'>
                      <label className='form-label required fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Vessel</label>
                      <SearchableSelect
                        className={`form-select ${errors.vesselId ? 'is-invalid' : ''}`}
                        value={selectedVesselId}
                        onChange={(e) => setSelectedVesselId(e.target.value)}
                        disabled={vesselSelectionLocked || (isSuperadmin && !selectedCompanyId)}
                      >
                        <option value=''>Select Vessel</option>
                        {vessels.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.fleet_name}
                          </option>
                        ))}
                      </SearchableSelect>
                      {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
                    </div>
                  )}

                  <div className='col-md-12'>
                    <label className='form-label required fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Item Name</label>
                    <input
                      type='text'
                      className={`form-control ${errors.itemName ? 'is-invalid' : ''}`}
                      value={itemName}
                      onChange={(e) => setItemName(e.target.value)}
                      placeholder='e.g., Rice, Coveralls, Office Subscription'
                    />
                    {errors.itemName && <div className='invalid-feedback'>{errors.itemName}</div>}
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Category</label>
                    <SearchableSelect
                      className='form-select'
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value)
                        setSubCategoryId('')
                      }}
                    >
                      <option value='STORES'>Stores</option>
                    </SearchableSelect>
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#111827'}}>
                      Sub Category (optional)
                    </label>
                    <SearchableSelect
                      className='form-select'
                      value={subCategoryId}
                      onChange={(e) => setSubCategoryId(e.target.value)}
                    >
                      <option value=''>Select Sub Category</option>
                      {subCategories.length > 0
                        ? subCategories.map((sc) => (
                            <option key={sc.id} value={sc.id}>
                              {sc.name}
                            </option>
                          ))
                        : staticSubCategoryOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                    </SearchableSelect>
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label required fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Unit</label>
                    <SearchableSelect
                      className={`form-select ${errors.unit ? 'is-invalid' : ''}`}
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                    >
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </SearchableSelect>
                    {errors.unit && <div className='invalid-feedback'>{errors.unit}</div>}
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Current Quantity</label>
                    <input
                      type='number'
                      className='form-control'
                      value={currentQty}
                      onChange={(e) => setCurrentQty(e.target.value)}
                      placeholder='0'
                    />
                  </div>

                  <div className='col-md-4'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Reorder Level</label>
                    <input
                      type='number'
                      className='form-control'
                      value={reorderLevel}
                      onChange={(e) => setReorderLevel(e.target.value)}
                      placeholder='0'
                    />
                  </div>

                  <div className='col-md-4'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Min Threshold</label>
                    <input
                      type='number'
                      className='form-control'
                      value={minThreshold}
                      onChange={(e) => setMinThreshold(e.target.value)}
                      placeholder='0'
                    />
                  </div>

                  <div className='col-md-4'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Max Threshold</label>
                    <input
                      type='number'
                      className='form-control'
                      value={maxThreshold}
                      onChange={(e) => setMaxThreshold(e.target.value)}
                      placeholder='0'
                    />
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Unit Cost</label>
                    <input
                      type='number'
                      step='0.01'
                      className='form-control'
                      value={unitCost}
                      onChange={(e) => setUnitCost(e.target.value)}
                      placeholder='0.00'
                    />
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{color: '#111827'}}>Location</label>
                    <SearchableSelect
                      className='form-select'
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                    >
                      <option value=''>Select Storage Location</option>
                      {storageLocations.map((storageLocation) => (
                        <option key={storageLocation.id} value={storageLocation.name}>
                          {storageLocation.name}
                        </option>
                      ))}
                    </SearchableSelect>
                    <div className='mt-2 text-muted fs-8'>
                      <div>
                        <span className='fw-semibold'>Sub Storage Location:</span>{' '}
                        {selectedStorageSubLocations || '-'}
                      </div>
                      <div className='mt-1'>
                        <span className='fw-semibold'>Segments:</span> {selectedStorageSegments || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0,
            background: '#fff',
          }}
        >
          <div
            style={{
              color: '#6B7280',
              fontSize: '0.88rem',
            }}
          >
            {showForm
              ? 'Review the details and save the item to inventory.'
              : 'Select “Add General Item” to open the item creation form.'}
          </div>

          <div className='d-flex gap-2'>
            <button
              type='button'
              className='btn btn-light btn-sm'
              onClick={handleClose}
              style={{minWidth: '100px'}}
            >
              Close
            </button>

            {showForm && (editingItem ? canUpdateEntries : canCreateEntries) && (
              <button
                type='button'
                className='btn btn_primary'
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{minWidth: '120px'}}
              >
                {isSubmitting ? 'Saving...' : editingItem ? 'Update Item' : 'Add Item'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GeneralItemModal
